import { t } from 'i18n';
import React from 'react';
import { ComboItem as IComboItem } from 'types';
import {
  CautionOutlined,
  CashColored,
  GiftColored,
  VoucherColored,
} from '@teko/ds-icon';
import { useScreen } from 'contexts';
import { getComboPromotionDetail } from 'helpers';
import { trackManually, getTrackingData } from '@teko/tracking';
import {
  styled,
  Button,
  SubTitle,
  LazyImage,
  Tag,
  CustomLink,
  Caption,
  Body,
  ThemeProps,
} from '@teko/ds-desktop';
import { ProductPrice } from '@teko/teko-ecommerce';
import { DiscoveryProduct } from '@teko/discovery';
interface ComboItemProps extends React.HTMLAttributes<HTMLDivElement> {
  onViewDetail: () => void | Promise<void>;
  onAddToCart: () => void | Promise<void>;
  comboItem: IComboItem;
}
export const ComboItem: React.FC<ComboItemProps> = ({
  comboItem,
  onAddToCart,
  onViewDetail,
  ...rest
}: ComboItemProps) => {
  const { trackingScreenName } = useScreen();
  const maxSlotProduct = comboItem.type === 'autogen' ? 4 : 3;
  const renderPrice = (product: DiscoveryProduct) => {
    const latestPrice = Number(product?.prices?.[0]?.latestPrice) || 0;
    const supplierRetailPrice =
      Number(product?.prices?.[0]?.supplierRetailPrice) || 0;
    return (
      <ProductPrice
        latestPrice={latestPrice}
        supplierRetailPrice={supplierRetailPrice}
        textColor="#434657"
      />
    );
  };
  const renderPromotion = () => {
    if (!comboItem.promotion) return null;
    const priceWithoutCombo = comboItem.items.reduce((acc, curr) => {
      const currLatestPrice =
        Number(curr.product?.prices?.[0]?.latestPrice) || 0;
      return acc + currLatestPrice * curr.quantity;
    }, 0);
    const promotionDetail = getComboPromotionDetail(
      comboItem.promotion,
      priceWithoutCombo,
    );
    const { tag, discount, title, description, icon, image } =
      promotionDetail || {};
    const bottom = (
      <StyledWrapperTag>
        <StyledTag type="ghost" variant="branding" content={tag || ''} />
        {!!discount && (
          <ProductPrice latestPrice={discount} textColor="#434657" />
        )}
        {!!title && (
          <StyledProductName level={1} line={2} color="#434657">
            {title}
          </StyledProductName>
        )}
        {!!description && (
          <StyledProductName level={1} line={2} color="#82869E">
            {description}
          </StyledProductName>
        )}
      </StyledWrapperTag>
    );
    if (icon) {
      return (
        <PromotionWrapper>
          {icon === 'cash' && (
            <StyledBgPromotion bgColor="#E7FBED">
              <CashColored size={60} />
            </StyledBgPromotion>
          )}
          {icon === 'gift' && (
            <StyledBgPromotion bgColor="#FFF2F0">
              <GiftColored size={60} />
            </StyledBgPromotion>
          )}
          {icon === 'voucher' && (
            <StyledBgPromotion bgColor="#FFF2F0">
              <VoucherColored size={60} />
            </StyledBgPromotion>
          )}
          {bottom}
        </PromotionWrapper>
      );
    } else {
      return (
        <PromotionWrapper>
          {image ? (
            <LazyImage
              imageWidth={200}
              width={125}
              height={125}
              contain
              borderRadius={1}
              src={image}
            />
          ) : null}
          {bottom}
        </PromotionWrapper>
      );
    }
  };
  const handleViewDetailCombo = () => {
    trackManually(
      'viewComboDetail',
      'productComboItem',
      trackingScreenName || 'ProductDetail',
      JSON.stringify({
        skus: comboItem.items.map((el) => el.product.productInfo.sku),
        promotionId: comboItem.promotionId,
        screenName: trackingScreenName,
        type: comboItem.type,
      }),
    );
    onViewDetail();
  };
  return (
    <Wrapper {...rest}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <ComboOverview>
          {comboItem.items.map((item, index, list) => {
            const link = {
              href: {
                pathname: `/products/${item.product.productInfo.sku}`,
              },
            };
            const exceedMaxSlot = list.length > maxSlotProduct;
            const isCompactItem = exceedMaxSlot && index === maxSlotProduct - 1;
            if (exceedMaxSlot && index >= maxSlotProduct) return null;
            const isLastItem = exceedMaxSlot
              ? index === maxSlotProduct - 1
              : index === list.length - 1;
            const renderedItem = (
              <>
                <StyledLazyImage>
                  <LazyImage
                    imageWidth={200}
                    width={125}
                    height={125}
                    contain
                    borderRadius={4}
                    src={item.product.productInfo.imageUrl}
                  />
                  {isCompactItem && (
                    <StyledRemainBg>+{list.length - index}</StyledRemainBg>
                  )}
                  {!isCompactItem && item.quantity > 1 && (
                    <QuantityTag>{item.quantity}x</QuantityTag>
                  )}
                </StyledLazyImage>
                <StyledProductName
                  level={1}
                  line={2}
                  color={isCompactItem ? '#82869E' : '#434657'}
                  style={
                    isCompactItem
                      ? {
                          textAlign: 'center',
                        }
                      : {}
                  }
                >
                  {isCompactItem
                    ? t('và {{var0}} sản phẩm khác', {
                        var0: list.length - index,
                      })
                    : item.product.productInfo.name}
                </StyledProductName>
                {isCompactItem ? null : renderPrice(item.product)}
              </>
            );
            return (
              <ItemWrapper
                key={index}
                className={!isLastItem ? 'not-last-item' : ''}
              >
                {isCompactItem ? (
                  <div onClick={handleViewDetailCombo}>{renderedItem}</div>
                ) : (
                  <CustomLink {...link}>
                    <div
                      {...getTrackingData({
                        index,
                        regionName: 'combo',
                        contentName: item.product.productInfo.sku,
                        target: 'productDetail',
                      })}
                    >
                      {renderedItem}
                    </div>
                  </CustomLink>
                )}
              </ItemWrapper>
            );
          })}
          {renderPromotion()}
        </ComboOverview>
        <SummaryCol>
          <Summary>
            <ProductPrice
              size="l"
              latestPrice={comboItem.latestPrice}
              supplierRetailPrice={comboItem.supplierRetailPrice}
            />
            <AddComboButtonWrapper
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'productComboItem',
                contentName: 'addToCart',
                target: trackingScreenName,
                payload: JSON.stringify({
                  skus: comboItem.items.map(
                    (el) => el.product.productInfo.sellerSku,
                  ),
                  promotionId: comboItem.promotionId,
                  screenName: trackingScreenName,
                  type: comboItem.type,
                }),
              })}
            >
              <StyledButton
                variant="secondary"
                block
                onClick={onAddToCart}
                className="att-add-product-combo-to-cart"
              >
                <SubTitle level={2} weight="medium">
                  {t('Thêm vào giỏ')}
                </SubTitle>
              </StyledButton>
            </AddComboButtonWrapper>
            <StyledButton
              block
              onClick={handleViewDetailCombo}
              className="att-view-detail-combo"
            >
              <SubTitle level={2} weight="medium">
                {t('Xem chi tiết')}
              </SubTitle>
            </StyledButton>
          </Summary>
        </SummaryCol>
      </div>
      {comboItem.type === 'dynamic' && (
        <StyledWarning>
          <CautionOutlined color="pending600" size={24} />
          <Body level={1} color="pending600" marginLeft="s8">
            {t('Không áp dụng cùng 1 số khuyến mãi khác')}
          </Body>
        </StyledWarning>
      )}
    </Wrapper>
  );
};
const Wrapper = styled.div`
  border-radius: 8px;
  padding: 1.5rem 0 1rem 1.5rem;
  background-color: #ffffff;
  margin-bottom: 1rem;
`;
const ComboOverview = styled.div`
  display: flex;
  flex: 3;
`;
const SummaryCol = styled.div`
  display: flex;
  flex: 1;
  border-left: 1px solid #e4e5f0;
`;
const Summary = styled.div`
  padding: 1.5rem;
  width: 100%;
`;
const StyledButton = styled(Button)`
  margin-top: 1rem;
`;
const ItemWrapper = styled.div`
  position: relative;
  margin-right: 3rem;
  width: 125px;
  cursor: pointer;
  &.not-last-item {
    &:after {
      content: '+';
      position: absolute;
      right: -1.75rem;
      top: 58px;
      color: #a7abc3;
      font-size: 18px;
      cursor: auto;
    }
  }
`;
const StyledLazyImage = styled.div`
  position: relative;
`;
const StyledRemainBg = styled.div`
  position: absolute;
  width: 125px;
  height: 125px;
  background-color: rgba(27, 29, 41, 0.5);
  color: #ffffff;
  z-index: 1;
  top: 0;
  left: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledProductName = styled(Caption)`
  margin-top: 8px;
`;
const StyledWarning = styled.div(
  ({ theme }: ThemeProps) => `
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-right: 1.5rem;
  background-color: ${theme?.colors.pending50};
  height: 40px;
  line-height: 40px;
  border-radius: 4px;
  padding-left: 12px;
`,
);
const PromotionWrapper = styled.div`
  position: relative;
  width: 125px;
  &:before {
    content: ' ';
    position: absolute;
    left: -1.5rem;
    color: #e4e5f0;
    border-right: 1px solid #a7abc3;
    height: 125px;
  }
`;
interface StyledBgPromotionProps {
  bgColor: string;
}
const StyledBgPromotion = styled.div<StyledBgPromotionProps>(
  ({ bgColor }: StyledBgPromotionProps) => `
  width: 125px;
  height: 125px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${bgColor};
  border-radius: 4px;
  &:before {
    content: ' ';
    position: absolute;
    left: -1.5rem;
    color: #e4e5f0;
    border-right: 1px solid #e4e5f0;
    height: 125px;
  }
`,
);
const StyledWrapperTag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
`;
const StyledTag = styled(Tag)`
  text-transform: uppercase;
`;
const QuantityTag = styled.div`
  position: absolute;
  height: 16px;
  background-color: #a7abc3;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: white;
  padding: 2px 5px;
  top: 8px;
  left: 8px;
`;

const AddComboButtonWrapper = styled.div``;
